import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import {
  Input,
  Modal,
  ModalCloseButton,
  Select,
  ToggleInput,
} from 'components/Shared';
import { MODAL_DISMISS_ACTION } from 'constants/Common';
import { useValidation } from 'hooks';
import React, {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import {
  FetchPhysicalDockActions as FetchActions,
  UpdatePhysicalDockActions as UpdateActions,
} from 'store/ducks/physicalDocks';
import { ListWarehouseBuildingsActions } from 'store/ducks/warehouseBuildings';
import { UpdatePhysicalDockValidator } from 'validators/PhysicalDocks';
import { findBuildingOption, groupBuildingsByArea } from '../helpers';
import * as S from './styles';

export interface Ref {
  selectDock: (id: number) => void;
}

interface Props {
  onUpdate?: () => void;
}

interface IPhysicalDockUpdateModal extends ForwardRefExoticComponent<
  Props & RefAttributes<Ref>
> {}

const PhysicalDockUpdateModal: IPhysicalDockUpdateModal = forwardRef<
  Ref,
  Props
>(({ onUpdate }, ref) => {
  const dispatch: AppDispatch = useDispatch();
  const formRef = useRef<FormHandles>(null);
  const generationRef = useRef(0);
  const activeAttemptRef = useRef<number | null>(null);
  const isOpenRef = useRef(false);
  const selectedIdRef = useRef<number | null>(null);
  const { handleApiErrors, handleFormErrors } = useValidation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [blocked, setBlocked] = useState(false);

  const { data: dock, loading: fetchingDock } = useSelector(
    (state: RootState) => state.fetchPhysicalDock
  );
  const { loading: updatingDock, validationErrors } = useSelector(
    (state: RootState) => state.updatePhysicalDock
  );
  const { data: buildings, loading: loadingBuildings } = useSelector(
    (state: RootState) => state.listWarehouseBuildings
  );
  const buildingOptions = useMemo(
    () => groupBuildingsByArea(buildings),
    [buildings]
  );
  const selectedDock = dock?.id === selectedId ? dock : null;

  const invalidateAttempts = useCallback(() => {
    generationRef.current += 1;
    activeAttemptRef.current = null;
  }, []);

  const isCurrentAttempt = useCallback(
    (attempt: number, dockId: number) =>
      isOpenRef.current &&
      selectedIdRef.current === dockId &&
      generationRef.current === attempt &&
      activeAttemptRef.current === attempt,
    []
  );

  const onSelectDock = useCallback(
    (id: number) => {
      invalidateAttempts();
      isOpenRef.current = true;
      selectedIdRef.current = id;
      formRef.current?.reset();
      setSelectedId(id);
      setIsOpen(true);
      dispatch(FetchActions.reset());
      dispatch(ListWarehouseBuildingsActions.reset());
      dispatch(FetchActions.request(id));
    },
    [dispatch, invalidateAttempts]
  );

  const onModalClose = useCallback(() => {
    isOpenRef.current = false;
    selectedIdRef.current = null;
    invalidateAttempts();
    setSelectedId(null);
    setBlocked(false);
    formRef.current?.reset();
    dispatch(FetchActions.reset());
    dispatch(UpdateActions.reset());
    dispatch(ListWarehouseBuildingsActions.reset());
    setIsOpen(false);
  }, [dispatch, invalidateAttempts]);

  useEffect(() => {
    if (!selectedDock || !formRef.current) return;

    dispatch(ListWarehouseBuildingsActions.request(selectedDock.warehouseId));
    setBlocked(Boolean(selectedDock.blockedAt));
    formRef.current.setData({
      name: selectedDock.name,
      number: selectedDock.number,
      blockedAt: selectedDock.blockedAt ? '1' : '0',
    });
  }, [dispatch, selectedDock]);

  useEffect(() => {
    if (!selectedDock || !formRef.current) return;
    formRef.current.setFieldValue(
      'buildingId',
      findBuildingOption(buildings, selectedDock.buildingId)
    );
  }, [buildings, selectedDock]);

  const onSuccess = useCallback(() => {
    onUpdate?.();
    onModalClose();
  }, [onModalClose, onUpdate]);

  const onSubmit = useCallback(
    async (data: any): Promise<void> => {
      if (
        !selectedDock ||
        updatingDock ||
        activeAttemptRef.current !== null ||
        !isOpenRef.current ||
        selectedIdRef.current !== selectedDock.id
      )
        return;

      const dockId = selectedDock.id;
      const attempt = generationRef.current + 1;
      generationRef.current = attempt;
      activeAttemptRef.current = attempt;

      try {
        formRef.current?.setErrors({});
        const validData =
          await new UpdatePhysicalDockValidator().schema.validate(data, {
            abortEarly: false,
          });
        if (!isCurrentAttempt(attempt, dockId)) return;

        dispatch(
          UpdateActions.request(
            dockId,
            validData,
            () => {
              if (!isCurrentAttempt(attempt, dockId)) return false;
              onSuccess();
              return true;
            },
            () => {
              if (!isCurrentAttempt(attempt, dockId)) return false;
              activeAttemptRef.current = null;
              return true;
            }
          )
        );
      } catch (error) {
        if (!isCurrentAttempt(attempt, dockId)) return;
        activeAttemptRef.current = null;
        handleFormErrors(error, formRef);
      }
    },
    [
      dispatch,
      handleFormErrors,
      isCurrentAttempt,
      onSuccess,
      selectedDock,
      updatingDock,
    ]
  );

  useEffect(() => {
    handleApiErrors(validationErrors, formRef);
  }, [handleApiErrors, validationErrors]);

  useImperativeHandle(ref, () => ({ selectDock: onSelectDock }), [
    onSelectDock,
  ]);

  useEffect(
    () => () => {
      isOpenRef.current = false;
      selectedIdRef.current = null;
      invalidateAttempts();
      dispatch(FetchActions.reset());
      dispatch(UpdateActions.reset());
    },
    [dispatch, invalidateAttempts]
  );

  return (
    <Modal isOpen={isOpen} onClickOutside={onModalClose}>
      <S.ModalContent style={{ maxWidth: '840px' }}>
        <S.ModalHeader>
          <h4>Editar Doca Física</h4>
          {fetchingDock && <S.ActivityIndicator />}
          <ModalCloseButton onClick={onModalClose} />
        </S.ModalHeader>
        {!!selectedDock && (
          <S.ModalBody>
            <Form ref={formRef} onSubmit={onSubmit}>
              <S.FormRow>
                <Input name="name" label="Nome" />
              </S.FormRow>
              <S.FormRow>
                <Input name="number" type="number" label="Número" />
                <Select
                  name="buildingId"
                  label="Prédio"
                  options={buildingOptions}
                  isLoading={loadingBuildings}
                  isClearable
                  menuPortalTarget={document.body}
                />
              </S.FormRow>
              <S.FormRow>
                <ToggleInput
                  id="updatePhysicalDockBlockedAt"
                  name="blockedAt"
                  label="Bloqueada"
                  checked={blocked}
                  onChange={(event) => setBlocked(event.target.checked)}
                />
              </S.FormRow>
            </Form>
            <S.FormActions>
              <S.Button type="button" mood="light" onClick={onModalClose}>
                {MODAL_DISMISS_ACTION}
              </S.Button>
              <S.Button
                type="submit"
                aria-label="Salvar"
                disabled={updatingDock}
                onClick={() => formRef.current?.submitForm()}
              >
                {updatingDock ? <S.ActivityIndicator /> : 'Salvar'}
              </S.Button>
            </S.FormActions>
          </S.ModalBody>
        )}
      </S.ModalContent>
    </Modal>
  );
});

export default PhysicalDockUpdateModal;
